/**
 * Locale selector barrel for module description data modules (queue #656 §R656-2).
 *
 * The ko/en maps are each ~1300 lines of rich JSX (~40 blocks), so both locales are
 * NOT statically imported — that would keep the inactive locale's markup resident in
 * the initial bundle for no reason. Instead each locale is loaded lazily (dynamic
 * import → its own chunk) only when a module catalog node's detail is first opened,
 * which keeps the initial LCP unaffected (SLO < 2.5s) while still caching per locale
 * so repeat opens are instant.
 */

import type { ReactNode } from 'react';

type DescriptionMap = Record<string, ReactNode>;

let koCache: DescriptionMap | null = null;
let enCache: DescriptionMap | null = null;
let koPromise: Promise<DescriptionMap> | null = null;
let enPromise: Promise<DescriptionMap> | null = null;

function isEnglish(lang: string): boolean {
  return lang.toLowerCase().startsWith('en');
}

/**
 * Resolve the module description map for the given i18n language, loading the
 * matching locale chunk on first use and caching it (per locale) thereafter.
 */
export function getModuleDescriptions(lang: string): Promise<DescriptionMap> {
  if (isEnglish(lang)) {
    if (enCache) return Promise.resolve(enCache);
    if (!enPromise) {
      enPromise = import('./module-descriptions.en').then((m) => {
        enCache = m.MODULE_DESCRIPTIONS_EN;
        return enCache;
      });
    }
    return enPromise;
  }

  if (koCache) return Promise.resolve(koCache);
  if (!koPromise) {
    koPromise = import('./module-descriptions.ko').then((m) => {
      koCache = m.MODULE_DESCRIPTIONS;
      return koCache;
    });
  }
  return koPromise;
}
